import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import isNil from 'lodash/isNil';

import Popover from 'components/popover/Popover';
import Sidenote from 'components/sidenote/Sidenote';
import ActionButton from 'components/buttons/action-button/ActionButton';

import FacebookButton from 'components/external-integration/facebook/button/FacebookButton';
import FacebookButtonHelp from 'components/external-integration/facebook/button/common/help/FacebookButtonHelp';

import { getAgencyIdForOrganization } from 'utils/navigation';
import { __t, applyTranslations } from 'utils/language';
import {
  isInfluencersFocusedOrganization,
  shouldInfluencerFillBankDetails,
  shouldInfluencerFillBusinessDetails
} from 'utils/organization';

import type { Organization, OrganizationSettings } from 'types';
import type { FacebookButtonState } from 'components/external-integration/facebook/button/types';

import './settings-message.scss';

applyTranslations(require('./settings-message.i18n.json'));

type OwnProps = {
  organizationId: number,
  organizationSettings: OrganizationSettings,
  showButton?: boolean,
  isSticky?: boolean
};

type StateProps = {
  mapByOrganizationId: {
    [x: number]: Organization
  },
  facebookButtonState: FacebookButtonState
};

type Props = OwnProps & StateProps;

class SettingsMessage extends React.PureComponent<Props> {
  getIsFacebookConnected = (): boolean => {
    const { facebookButtonState } = this.props;

    return facebookButtonState.isFBLoginActive && !facebookButtonState.isFBLoginExpired;
  };

  getMissingType(): string | null {
    const { organizationSettings } = this.props;
    const isFBConnected = this.getIsFacebookConnected();
    const missingBankDetails = shouldInfluencerFillBankDetails(organizationSettings);
    const missingBusinessDetails = shouldInfluencerFillBusinessDetails(organizationSettings);
    let missingType = '';

    if (missingBankDetails) {
      missingType = missingBusinessDetails ? 'all' : 'bank';
    }

    if (missingBusinessDetails) {
      missingType = 'business';
    }

    if (!isFBConnected && !missingBankDetails && !missingBusinessDetails) {
      missingType = 'facebook';
    }

    return missingType || null;
  }

  renderStandardButton(missingType: string, settingsDetailsUrl: string) {
    const { showButton } = this.props;

    return showButton !== false && (
      <ActionButton
        color="secondary"
        link={settingsDetailsUrl}
      >
        {__t(`settings_message.influencer.${missingType}.details_button`)}
      </ActionButton>
    );
  }

  renderPopover = (): React.ReactNode => (
    <Popover
      classNamePopover="settings-message__popover"
      showQuestionMark
    >
      <FacebookButtonHelp />
    </Popover>
  );

  renderInfluencerBanner(settingsDetailsUrl: string) {
    const missingType = this.getMissingType();

    if (isNil(missingType)) {
      return null;
    }

    return (
      <div className="settings-message--influencer">
        <div>
          {__t(`settings_message.influencer.${missingType}.campaigns_list_information_needed`)}
          {
            missingType === 'facebook' &&
              this.renderPopover()
          }
        </div>
        {
          missingType === 'facebook'
            ? <FacebookButton />
            : this.renderStandardButton(missingType, settingsDetailsUrl)
        }
      </div>
    );
  }

  renderBrandBanner(settingsDetailsUrl: string) {
    return (
      <div className="settings-message--brand">
        <div>
          {__t('settings_message.brand.campaigns_list_information_needed')}
        </div>
        <ActionButton
          link={settingsDetailsUrl}
        >
          {__t('settings_message.brand.campaigns_list_complete_settings_page')}
        </ActionButton>
      </div>
    );
  }

  render(): React.ReactNode {
    const {
      isSticky,
      organizationId,
      mapByOrganizationId
    } = this.props;

    if (!organizationId) {
      return null;
    }

    const organization = mapByOrganizationId[organizationId];
    const agencyId = getAgencyIdForOrganization(organization);
    const isInfluencer = isInfluencersFocusedOrganization(organization);

    const settingsDetailsUrl = agencyId && !organization?.influencerProfiles?.length ?
      `/agency/${agencyId}/settings/details` :
      `/organizations/${organizationId}/settings/details`;

    const classes = classnames('settings-message__sidenote', {
      'settings-message__sidenote--sticky': isSticky && isInfluencer
    });

    const missingType = this.getMissingType();
    const shouldShowFacebookMessage = !this.getIsFacebookConnected();

    if (isNil(missingType) || !shouldShowFacebookMessage) {
      return null;
    }

    return (
      <Sidenote
        withCenteredIcon
        type="info"
        className={classes}
        layout="horizontal"
      >
        {
          isInfluencer
            ? this.renderInfluencerBanner(settingsDetailsUrl)
            : this.renderBrandBanner(settingsDetailsUrl)
        }
      </Sidenote>
    );
  }
}

const mapStateToProps = ({
  organizations,
  facebookButton
}): StateProps => ({
  mapByOrganizationId: organizations.mapByOrganizationId,
  facebookButtonState: facebookButton
});

export default connect(mapStateToProps)(SettingsMessage);
